import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { User, Blocks, Eye, HeartHandshake, Users, ShieldCheck, HandHeart, Puzzle, Scale, GraduationCap, Briefcase, ArrowRight, MapPin, Mail, Phone, Clock, Video } from "lucide-react";
import Marquee from "@/components/site/Marquee";
import { Reveal, SectionHeading, EASE } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/Logo";
import { SERVICES, CONTACT, BOOKING_URL } from "@/data/services";

const ICONS = [User, Blocks, Eye, HeartHandshake, Users, ShieldCheck, HandHeart, Puzzle, Scale, GraduationCap, Briefcase];

const HERO_IMG = "https://images.unsplash.com/photo-1611255552402-4f772d00621b?crop=entropy&cs=srgb&fm=jpg&q=85";

const FUNDING = [
  "Accredited Mental Health Social Worker",
  "Registered NDIS Provider",
  "Medicare rebate of $89.50 available with an eligible referral",
  "Private health rebates may be available",
  "Open Arms and NSW Victims Assist clients accepted",
];

const STEPS = [
  {
    n: "01",
    title: "Choose a service",
    text: "Read about the support that most closely matches your needs.",
  },
  {
    n: "02",
    title: "Contact Eucalypt Therapy",
    text: "Book online, send an enquiry or ask your referrer to contact the practice.",
  },
  {
    n: "03",
    title: "Discuss your needs",
    text: "We will confirm whether the service is appropriate, explain the fees and arrange the next step.",
  },
];

function HeroLine({ children, delay }) {
  return (
    <span className="mask-line">
      <motion.span
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const sprigY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-euca-mist">
        <motion.div style={{ y: sprigY }} className="pointer-events-none absolute -left-16 top-24 text-euca-line">
          <Sprig className="h-80 w-80" />
        </motion.div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-28 lg:pb-36">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="text-[11px] font-semibold uppercase tracking-[0.35em] text-euca"
                data-testid="hero-eyebrow"
              >
                Gold Coast · Canberra
              </motion.p>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-[4.2rem] font-medium leading-[1.08] tracking-tight text-euca-dark" data-testid="hero-heading">
                <HeroLine delay={0.15}>Trauma-informed therapy</HeroLine>
                <HeroLine delay={0.28}>and assessment</HeroLine>
                <HeroLine delay={0.41}>
                  <span className="italic text-euca">on the Gold Coast</span>
                </HeroLine>
                <HeroLine delay={0.54}>
                  <span className="italic text-euca">and in Canberra</span>
                </HeroLine>
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                className="mt-7 max-w-xl"
                data-testid="hero-mission"
              >
                <p className="font-serif text-lg sm:text-xl italic leading-relaxed text-euca-dark">
                  &ldquo;The eucalypt tree, so unique to the Australian landscape, is not impervious to fire. But it
                  weathers the hardship and looks to the fertile ground after the flames die down to grow back
                  stronger.&rdquo;
                </p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Our mission at Eucalypt Therapy is to empower our clients to see that every hardship is an
                  opportunity for growth — and that at the heart of everyone is the potential to come back stronger,
                  like the eucalypt.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <a
                  href="#services"
                  data-testid="hero-explore-services-button"
                  className="rounded-full border border-euca px-7 py-3.5 text-sm font-semibold text-euca transition-all duration-300 hover:bg-euca hover:text-white"
                >
                  Explore Our Services
                </a>
                <a
                  href="/book"
                  data-testid="hero-book-appointment-button"
                  className="rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-euca-dark hover:shadow-md"
                >
                  Book an Appointment
                </a>
                <Link
                  to="/contact"
                  data-testid="hero-send-enquiry-button"
                  className="rounded-full border border-euca-line bg-white px-7 py-3.5 text-sm font-semibold text-charcoal transition-all duration-300 hover:border-euca hover:text-euca"
                >
                  Send an Enquiry
                </Link>
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.95 }}
                className="mt-6 text-sm text-mutedsoft/80"
              >
                Medicare, private health and NDIS funding may be available depending on the service.
              </motion.p>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                className="relative"
              >
                <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] border border-euca-line shadow-2xl shadow-euca-dark/10">
                  <motion.img
                    style={{ y: imgY, scale: 1.12 }}
                    src={HERO_IMG}
                    alt="Soft light through eucalyptus foliage"
                    className="h-[420px] w-full object-cover sm:h-[520px]"
                    data-testid="hero-image"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 rounded-2xl border border-euca-line bg-white/90 px-5 py-4 backdrop-blur-md shadow-lg">
                  <p className="font-serif text-lg italic text-euca-dark">Grounded. Calm. Evidence-informed.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Services */}
      <section id="services" className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <SectionHeading
            eyebrow="Services"
            title="What kind of support are you looking for?"
            lead="Compassionate, evidence-informed support for individuals, couples, families, veterans and NDIS participants — organised around what you need, not clinical jargon. With rooms in Ashmore on the Gold Coast and Deakin in Canberra, plus in-home and telehealth services across the Gold Coast, Logan, Brisbane and surrounding areas."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-euca-line bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-euca-sage hover:shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-euca-badge text-euca transition-colors duration-300 group-hover:bg-euca group-hover:text-white">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                      <span className="font-serif text-sm italic text-euca-sage">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <h3 className="mt-6 font-serif text-2xl font-medium text-euca-dark">{s.name}</h3>
                    <p className="mt-3 flex-1 text-base leading-relaxed text-mutedsoft">{s.blurb}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-euca">
                      {s.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Family law highlight */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-euca-dark px-6 py-12 sm:px-14 sm:py-16" data-testid="family-law-banner">
              <div className="pointer-events-none absolute -right-10 -bottom-12">
                <Sprig variant="dark" className="h-64 w-64" />
              </div>
              <div className="relative grid items-center gap-10 lg:grid-cols-12">
                <div className="lg:col-span-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca-sage">
                    For parents, solicitors &amp; the court
                  </p>
                  <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-medium text-euca-mist">
                    Family law &amp; parental capacity reports
                  </h2>
                  <p className="mt-4 max-w-2xl text-base sm:text-lg leading-relaxed text-euca-mist/70">
                    Independent family reports and parental capacity assessments — structured interviews with each
                    parent, parent–child observations and a dedicated child-only session, brought together in a
                    clear written report with professional recommendations.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link
                    to="/services/family-law-reports"
                    data-testid="family-law-banner-button"
                    className="inline-flex items-center gap-2 rounded-full bg-euca-mist px-7 py-3.5 text-sm font-semibold text-euca-dark transition-all duration-300 hover:bg-white"
                  >
                    Family Law Reports <ArrowRight className="h-4 w-4" />
                  </Link>
                  <p className="mt-4 text-sm text-euca-mist/60">$4,200 + GST · timeframes confirmed on engagement</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* About */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative">
                  <div className="overflow-hidden rounded-[2rem] border border-euca-line">
                    <img
                      src="https://images.unsplash.com/photo-1611255550543-b5ecb01dfddc?crop=entropy&cs=srgb&fm=jpg&q=85"
                      alt="Eucalyptus leaves in soft natural light"
                      loading="lazy"
                      className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
                      data-testid="about-image"
                    />
                  </div>
                  <div className="absolute -right-6 -top-6">
                    <Sprig variant="sage" className="h-28 w-28" />
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="About Eucalypt Therapy"
                title="Professional care grounded in compassion"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Eucalypt Therapy is a trauma-informed practice based in Ashmore on the Gold Coast. We support
                  individuals, couples and families experiencing complex mental health, relationship, disability
                  and family-system challenges.
                </p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Services are led by Lucie Tursicova, an Accredited Mental Health Social Worker with extensive
                  experience in trauma-informed psychotherapy, child protection, mental health, family assessment
                  and Positive Behaviour Support.
                </p>
                <Link
                  to="/about"
                  data-testid="meet-lucie-button"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
                >
                  Meet Lucie <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* How to access support */}
      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <SectionHeading
            eyebrow="How to access support"
            title="Three simple steps"
          />
          <div className="mt-14 grid gap-10 md:grid-cols-3" data-testid="steps-grid">
            {STEPS.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.12}>
                <div className="border-t-2 border-euca-line pt-8">
                  <span className="font-serif text-5xl font-medium italic text-euca-sage/70">{step.n}</span>
                  <h3 className="mt-5 font-serif text-2xl font-medium text-euca-dark">{step.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-mutedsoft">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Funding & registration */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <SectionHeading
            eyebrow="Funding & registration"
            title="Clear about credentials and costs"
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="funding-list">
            {FUNDING.map((f, i) => (
              <Reveal key={f} delay={i * 0.06} y={14}>
                <div className="flex items-start gap-3 rounded-xl border border-euca-line bg-euca-mist/60 px-5 py-4">
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-euca" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <p className="text-sm leading-relaxed text-charcoal/85">{f}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} y={14}>
              <Link
                to="/fees-funding"
                data-testid="view-fees-funding-button"
                className="flex h-full items-center justify-center gap-2 rounded-xl bg-euca px-5 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
              >
                View Fees and Funding <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Eucalypt Calm app teaser */}
      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-euca-dark px-6 py-12 sm:px-14 sm:py-16" data-testid="calm-app-banner">
              <div className="pointer-events-none absolute -right-10 -bottom-12">
                <Sprig variant="dark" className="h-64 w-64" />
              </div>
              <div className="relative max-w-2xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca-sage">Coming soon</p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-medium text-euca-mist">
                  Eucalypt Calm — our upcoming app
                </h2>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-euca-mist/70">
                  Breathwork tutorials, meditation, guided meditation and journaling — gentle tools from Eucalypt
                  Therapy, wherever you are.
                </p>
                <Link
                  to="/contact?service=calm-app"
                  data-testid="calm-app-button"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-euca-mist px-7 py-3.5 text-sm font-semibold text-euca-dark transition-all duration-300 hover:bg-white"
                >
                  Register your interest <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA + contact details */}
      <section className="relative overflow-hidden bg-euca-dark">
        <div className="pointer-events-none absolute -right-14 bottom-0">
          <Sprig variant="dark" className="h-96 w-96" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <SectionHeading
            dark
            eyebrow="Contact"
            title="Not sure which service you need?"
            lead="Contact Eucalypt Therapy and we can help you identify the most appropriate next step."
          />
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="final-send-enquiry-button"
                className="rounded-full bg-euca-mist px-7 py-3.5 text-sm font-semibold text-euca-dark transition-all duration-300 hover:bg-white"
              >
                Send an Enquiry
              </Link>
              <a
                href="/book"
                data-testid="final-book-appointment-button"
                className="rounded-full border border-euca-mist/40 px-7 py-3.5 text-sm font-semibold text-euca-mist transition-all duration-300 hover:border-euca-mist"
              >
                Book an Appointment
              </a>
            </div>
          </Reveal>
          <div className="mt-16 grid gap-8 border-t border-euca-mist/10 pt-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="contact-details-strip">
            {[
              { icon: MapPin, label: "Visit — Gold Coast", value: CONTACT.address, href: null, id: "detail-address" },
              { icon: MapPin, label: "Visit — Canberra", value: CONTACT.addressCanberra, href: null, id: "detail-address-canberra" },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "detail-email" },
              { icon: Phone, label: "Call", value: CONTACT.phone, href: CONTACT.phoneHref, id: "detail-phone" },
              { icon: Clock, label: "Opening hours", value: "By appointment, Monday to Friday", href: null, id: "detail-hours" },
              { icon: Video, label: "Telehealth", value: "Gold Coast, Logan, Brisbane, Canberra and surrounding suburbs", href: null, id: "detail-telehealth" },
            ].map(({ icon: Icon, label, value, href, id }, i) => (
              <Reveal key={id} delay={i * 0.07} y={14}>
                <div data-testid={id}>
                  <Icon className="h-5 w-5 text-euca-sage" strokeWidth={1.5} />
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-euca-sage">{label}</p>
                  {href ? (
                    <a href={href} className="mt-2 block text-sm leading-relaxed text-euca-mist/80 hover:text-euca-mist">{value}</a>
                  ) : (
                    <p className="mt-2 text-sm leading-relaxed text-euca-mist/80">{value}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
